import React from "react";
import { Link } from "react-router-dom";

const LAST_UPDATED = "April 4, 2026";
const COMPANY = "VaWeNa, LLC.";
const EMAIL = "privacy@vawena.ai";
const ADDRESS = "VaWeNa, LLC, United States";

/** Primary body copy — readable in both modes */
const prose = "text-gray-700 dark:text-slate-300";
const proseMuted = "text-gray-600 dark:text-slate-400";
const heading = "text-gray-950 dark:text-white";
const emphasis = "font-semibold text-gray-900 dark:text-slate-100";

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="mb-12 scroll-mt-28">
      <h2
        className={`text-lg sm:text-xl font-semibold ${heading} mb-4 pl-4 border-l-[3px] border-blue-500 dark:border-blue-400/90`}
      >
        {title}
      </h2>
      <div className={`${prose} leading-relaxed space-y-3 text-[15px] sm:text-base pl-1`}>{children}</div>
    </section>
  );
}

function KeyPoint({
  children,
  accent = "neutral",
}: {
  children: React.ReactNode;
  accent?: "emerald" | "blue" | "neutral";
}) {
  const border =
    accent === "emerald"
      ? "border-emerald-500/70 dark:border-emerald-400/55"
      : accent === "blue"
        ? "border-blue-500/70 dark:border-blue-400/55"
        : "border-gray-300 dark:border-white/20";
  const bg =
    accent === "emerald"
      ? "dark:bg-emerald-500/[0.06] bg-emerald-50/60"
      : accent === "blue"
        ? "dark:bg-blue-500/[0.06] bg-blue-50/70"
        : "dark:bg-white/[0.03] bg-gray-50/90";
  return (
    <div className={`border-l-4 ${border} ${bg} pl-4 py-3 pr-4 rounded-r-lg mb-4`}>{children}</div>
  );
}

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50/90 via-[#f0f4ff] to-[#e8eefc] dark:from-vw-bg dark:via-vw-bg dark:to-vw-bg">
      <header className="relative overflow-hidden border-b border-blue-200/60 dark:border-white/[0.07]">
        <div
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_90%_60%_at_50%_-10%,rgba(59,130,246,0.18),transparent)] dark:bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(59,130,246,0.12),transparent)]"
          aria-hidden
        />
        <div className="relative pt-16 pb-12 px-4 sm:px-8">
          <div className="max-w-[680px] mx-auto text-center">
            <p className={`text-[11px] sm:text-xs font-semibold uppercase tracking-[0.28em] text-blue-700 dark:text-blue-400 mb-4`}>
              Data & privacy notice
            </p>
            <h1 className={`text-3xl sm:text-4xl md:text-[2.75rem] font-bold tracking-tight ${heading} mb-4 leading-[1.15]`}>
              Privacy Policy
            </h1>
            <div className={`flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-sm ${proseMuted}`}>
              <span>How we handle your information</span>
              <span className="hidden sm:inline text-gray-300 dark:text-slate-600">·</span>
              <time dateTime="2026-04-04">Last updated {LAST_UPDATED}</time>
            </div>
            <div className="mt-8 h-px w-16 mx-auto bg-gradient-to-r from-transparent via-blue-500/50 to-transparent" aria-hidden />
          </div>
        </div>
      </header>

      <div className="max-w-[720px] mx-auto px-4 sm:px-6 pb-24 pt-10">
        <div className="dark:bg-blue-500/[0.07] bg-blue-50/80 border dark:border-blue-500/25 border-blue-200/90 rounded-xl p-6 sm:p-7 mb-12 text-[15px] sm:text-base leading-relaxed text-gray-700 dark:text-slate-300">
          <p>
            {COMPANY} ("<strong className={emphasis}>VaWeNa</strong>," "<strong className={emphasis}>we</strong>," "<strong className={emphasis}>our</strong>," or "<strong className={emphasis}>us</strong>") operates the <strong className={emphasis}>vawena.ai</strong> website and the VaWeNa web application (collectively, the "<strong className={emphasis}>Service</strong>"). This Privacy Policy explains how we collect, use, disclose, and protect your information when you use our Service. By using VaWeNa, you agree to this policy.
          </p>
        </div>

        <nav
          aria-label="Table of contents"
          className="mb-14 overflow-hidden rounded-xl border border-gray-200/90 dark:border-white/10 bg-white/95 dark:bg-white/[0.03] shadow-md shadow-blue-900/5 dark:shadow-none"
        >
          <p
            className={`border-b border-gray-100 dark:border-white/10 px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] ${proseMuted}`}
          >
            Jump to section
          </p>
          <ol className="divide-y divide-gray-100 dark:divide-white/10 text-[15px]">
            {[
              ["1. Information We Collect", "#collect"],
              ["2. How We Use Your Information", "#use"],
              ["3. Sharing Your Information", "#sharing"],
              ["4. Banking Data & Plaid / Flinks", "#banking"],
              ["5. Payments & Stripe", "#payments"],
              ["6. Data Security", "#security"],
              ["7. Data Retention", "#retention"],
              ["8. Your Rights & Choices", "#rights"],
              ["9. Cookies & Tracking", "#cookies"],
              ["10. Children's Privacy", "#children"],
              ["11. Changes to This Policy", "#changes"],
              ["12. Contact Us", "#contact"],
            ].map(([label, href]) => (
              <li key={href}>
                <a
                  href={href}
                  className="block px-5 py-3 text-blue-800 hover:bg-blue-50/80 hover:text-blue-950 dark:text-blue-300/95 dark:hover:bg-white/[0.05] dark:hover:text-white transition-colors"
                >
                  {label}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <Section id="collect" title="1. Information We Collect">
          <p>
            <strong className={emphasis}>Account information:</strong> When you register, we collect your name, email address, and encrypted password.
          </p>
          <p>
            <strong className={emphasis}>Financial data you provide:</strong> Net worth entries, income sources, asset values, liability balances, and property details you manually enter into the platform.
          </p>
          <p>
            <strong className={emphasis}>Bank account data (via Plaid / Flinks):</strong> If you choose to connect a bank account, we receive account numbers (masked), institution names, balances, and transaction history from our banking partners. See Section 4 for full details.
          </p>
          <p>
            <strong className={emphasis}>Property data:</strong> Addresses, purchase prices, rental income, expenses, mortgages, and tenant information you enter into PortfolioIQ.
          </p>
          <p>
            <strong className={emphasis}>Usage data:</strong> Pages visited, features used, device type, browser, IP address, and timestamps, collected automatically via server logs and analytics tools.
          </p>
          <p>
            <strong className={emphasis}>Communications:</strong> If you contact us, we retain those messages and your contact details.
          </p>
        </Section>

        <Section id="use" title="2. How We Use Your Information">
          <p>We use your information to:</p>
          <ul className="list-disc pl-5 space-y-1.5 marker:text-blue-600/80 dark:marker:text-blue-400/80">
            <li>Provide, operate, and improve the VaWeNa platform and its features.</li>
            <li>Calculate your net worth, buying power, DSCR, cash flow, and other financial metrics.</li>
            <li>Generate AI-powered insights, alerts, and recommendations.</li>
            <li>Send transactional emails (receipts, alerts, password resets).</li>
            <li>Send product updates and marketing communications (you may opt out at any time).</li>
            <li>Process payments and manage subscriptions via Stripe.</li>
            <li>Detect, investigate, and prevent fraud, security incidents, and abuse.</li>
            <li>Comply with legal obligations.</li>
          </ul>
          <p>
            We do <strong className={emphasis}>not</strong> sell your personal or financial data to third parties.
          </p>
        </Section>

        <Section id="sharing" title="3. Sharing Your Information">
          <p>We share your information only with:</p>
          <ul className="list-disc pl-5 space-y-1.5 marker:text-blue-600/80 dark:marker:text-blue-400/80">
            <li>
              <strong className={emphasis}>Service providers:</strong> Supabase (database/auth), Plaid and Flinks (bank connectivity), Stripe (payments), Resend (email delivery), Google Cloud (infrastructure). These providers process data only on our behalf under data processing agreements.
            </li>
            <li>
              <strong className={emphasis}>Legal obligations:</strong> We may disclose information when required by law, court order, or to protect our legal rights.
            </li>
            <li>
              <strong className={emphasis}>Business transfers:</strong> In a merger, acquisition, or asset sale, your data may be transferred. We will notify you before your data is subject to a new privacy policy.
            </li>
            <li>
              <strong className={emphasis}>With your consent:</strong> For any other purpose, only with your explicit permission.
            </li>
          </ul>
        </Section>

        <Section id="banking" title="4. Banking Data & Plaid / Flinks">
          <KeyPoint accent="emerald">
            <p>
              Your bank credentials are <strong className={emphasis}>never stored by VaWeNa</strong>. They are handled exclusively by Plaid or Flinks using bank-level encryption.
            </p>
          </KeyPoint>
          <p>When you link a financial account:</p>
          <ul className="list-disc pl-5 space-y-1.5 marker:text-blue-600/80 dark:marker:text-blue-400/80">
            <li>You authenticate directly with your bank through Plaid's or Flinks' secure interface.</li>
            <li>We receive only tokenized access to read-only account data (balances, transactions, account names).</li>
            <li>We store masked account identifiers and encrypted access tokens.</li>
            <li>You can disconnect a bank account at any time from the Connected Accounts settings screen. Disconnecting immediately revokes our access token and removes account data from active dashboards.</li>
          </ul>
          <p>
            Plaid's privacy policy is available at <span className="text-blue-800 dark:text-blue-400">plaid.com/legal/privacy-policy</span>. Flinks' privacy policy is available at <span className="text-blue-800 dark:text-blue-400">flinks.com/privacy-policy</span>.
          </p>
        </Section>

        <Section id="payments" title="5. Payments & Stripe">
          <KeyPoint accent="blue">
            <p>
              VaWeNa uses <strong className={emphasis}>Stripe</strong> to process all payments. We do not store your full card number, CVV, or payment credentials on our servers.
            </p>
          </KeyPoint>
          <p>
            Stripe may collect billing address, card details, and transaction data. Stripe is a PCI-DSS Level 1 certified payment processor. Their privacy policy is available at <span className="text-blue-800 dark:text-blue-400">stripe.com/privacy</span>.
          </p>
          <p>We retain subscription status, plan type, billing cycle, and transaction IDs to manage your account.</p>
        </Section>

        <Section id="security" title="6. Data Security">
          <p>We implement industry-standard security measures including:</p>
          <ul className="list-disc pl-5 space-y-1.5 marker:text-blue-600/80 dark:marker:text-blue-400/80">
            <li>TLS/HTTPS encryption for all data in transit.</li>
            <li>AES-256 encryption for sensitive credentials at rest.</li>
            <li>Row-level security (RLS) in our database — your data is isolated from other users.</li>
            <li>JWT-based authentication with short-lived tokens.</li>
            <li>Google Cloud Armor WAF protecting our API endpoints.</li>
            <li>Regular dependency and vulnerability scanning.</li>
          </ul>
          <p>
            No system is 100% secure. If you believe your account has been compromised, contact us immediately at <strong className={emphasis}>{EMAIL}</strong>.
          </p>
        </Section>

        <Section id="retention" title="7. Data Retention">
          <p>We retain your data for as long as your account is active or as needed to provide the Service. If you delete your account:</p>
          <ul className="list-disc pl-5 space-y-1.5 marker:text-blue-600/80 dark:marker:text-blue-400/80">
            <li>Active data (financial entries, property records) is deleted within 30 days.</li>
            <li>Bank connection tokens are revoked immediately.</li>
            <li>Anonymized, aggregated analytics data may be retained indefinitely.</li>
            <li>We may retain data longer if required by applicable law (e.g., financial record-keeping regulations).</li>
          </ul>
        </Section>

        <Section id="rights" title="8. Your Rights & Choices">
          <KeyPoint accent="neutral">
            <p>Depending on your jurisdiction, you may have the right to access, correct, delete, or export your data.</p>
          </KeyPoint>
          <p>You have the right to:</p>
          <ul className="list-disc pl-5 space-y-1.5 marker:text-blue-600/80 dark:marker:text-blue-400/80">
            <li>
              <strong className={emphasis}>Access:</strong> Request a copy of the personal data we hold about you.
            </li>
            <li>
              <strong className={emphasis}>Correction:</strong> Request correction of inaccurate data.
            </li>
            <li>
              <strong className={emphasis}>Deletion:</strong> Request deletion of your account and associated data.
            </li>
            <li>
              <strong className={emphasis}>Portability:</strong> Request an export of your data in a machine-readable format.
            </li>
            <li>
              <strong className={emphasis}>Opt-out of marketing:</strong> Unsubscribe from marketing emails using the link in any email, or contact us.
            </li>
            <li>
              <strong className={emphasis}>California (CCPA):</strong> California residents have additional rights including the right to know, delete, and opt out of the "sale" of personal information (we do not sell personal information).
            </li>
            <li>
              <strong className={emphasis}>GDPR:</strong> EU/EEA residents have rights under the GDPR. To exercise them, email {EMAIL}.
            </li>
          </ul>
        </Section>

        <Section id="cookies" title="9. Cookies & Tracking">
          <p>We use the following types of cookies:</p>
          <ul className="list-disc pl-5 space-y-1.5 marker:text-blue-600/80 dark:marker:text-blue-400/80">
            <li>
              <strong className={emphasis}>Essential cookies:</strong> Required for authentication and core platform functionality.
            </li>
            <li>
              <strong className={emphasis}>Analytics cookies:</strong> Help us understand how the platform is used (e.g., page visits, feature usage). These are first-party and do not include personal identifiers.
            </li>
          </ul>
          <p>You can control cookies through your browser settings. Disabling essential cookies will prevent you from logging in.</p>
        </Section>

        <Section id="children" title="10. Children's Privacy">
          <p>VaWeNa is not directed to children under 18 years of age. We do not knowingly collect personal information from children under 18. If you believe a child has provided us personal information, contact us at {EMAIL} and we will delete it promptly.</p>
        </Section>

        <Section id="changes" title="11. Changes to This Policy">
          <KeyPoint accent="neutral">
            <p>
              We may update this Privacy Policy from time to time. If we make material changes, we will notify you by email or by a prominent notice on the platform at least 14 days before the changes take effect. The updated date at the top of this page reflects when the policy was last revised.
            </p>
          </KeyPoint>
        </Section>

        <Section id="contact" title="12. Contact Us">
          <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50/90 dark:bg-white/[0.03] p-6 sm:p-7">
            <p className={`${emphasis} mb-3`}>{COMPANY}</p>
            <p className="mb-2">{ADDRESS}</p>
            <p>
              Email:{" "}
              <a
                href={`mailto:${EMAIL}`}
                className="text-blue-800 hover:text-blue-950 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
              >
                {EMAIL}
              </a>
            </p>
            <p className="mt-4 text-[15px]">
              For privacy requests, please email us with the subject line <strong className={emphasis}>"Privacy Request"</strong> and include your registered email address. We will respond within 30 days.
            </p>
          </div>
        </Section>

        <div className={`border-t border-gray-200 dark:border-white/10 pt-8 mt-12 flex flex-wrap gap-x-8 gap-y-2 text-sm ${proseMuted}`}>
          <Link to="/terms" className="hover:text-gray-900 dark:hover:text-white transition-colors">
            Terms of Service
          </Link>
          <Link to="/" className="hover:text-gray-900 dark:hover:text-white transition-colors">
            Back to Home
          </Link>
          <Link to="/contact" className="hover:text-gray-900 dark:hover:text-white transition-colors">
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
